import React from "react";
// Common components
import {
  AddButton,
  Modal,
  Button,
  DynamicDropdown,
  FullScreenLoading,
} from "src/Components";
// Local components
import FundNoRiskTable from "src/Pages/FundNoRisk/FundNoRiskTable";
import { FixedIncomeBulkActions } from "src/Pages/FundNoRisk/Additional";
import AddEditTask from "src/Pages/FundNoRisk/AddEditTask";
import Setting from "src/Pages/FundNoRisk/Setting";
import Sandoogh from "src/Pages/FundNoRisk/Sandoogh";
// Hooks
import { useAppSelector, useAppDispatch } from "src/App/hooks";
// Reducers
import {
  setFunds,
  setIsfundNoRiskAddEditOpen,
  setAddEditFundNotRiskFormValues,
} from "src/Features/FundNoRisk/fundNoRiskSlice";
// Interfaces
import {
  IFundNoRiskAddTaskDTO,
  IFundNoRiskSearchParam,
} from "src/Interfaces/fundNoRisk";
// requests
import { getSandooghsDropDown } from "src/Requests/fundNoRiskTask";
import { getPortfolioGroupDropDown } from "src/Requests/portfolioGroups";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
import UsersDropdown from "src/Components/Dropdown/Users/UsersDropdown";

type IModal = "isSettingModalOpen" | "isSandooghModalOpen";

const defaultModalState = {
  isSettingModalOpen: false,
  isSandooghModalOpen: false,
};
const FundNoRisk: React.FC = () => {
  // hooks
  const dispatch = useAppDispatch();
  const isAddEditModalOpen = useAppSelector(
    (store) => store.fundNoRisk.isAddEditOpen,
  );
  // states
  const [loading, setLoading] = React.useState<boolean>(false);
  const [refreshFlag, setRefreshFlag] = React.useState(false);
  const [selectedRowsIds, setSelectedRowsIds] = React.useState<number[]>([]);
  const [searchParams, setSearchParams] = React.useState<IFundNoRiskSearchParam>(
    {} as IFundNoRiskSearchParam,
  );
  const [modal, setModal] = React.useState(defaultModalState);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParams((previous) => ({
      ...previous,
      [name]: value === "" || value == null ? null : Number(value),
    }));
  };

  const loadFunds = React.useCallback(() => {
    return getSandooghsDropDown()
      .then((list) => {
        dispatch(setFunds(list));
      })
      .catch(packetErrorHandler);
  }, [dispatch]);

  React.useEffect(() => {
    setLoading(true);
    loadFunds().finally(() => setLoading(false));
  }, [loadFunds]);

  const refreshTable = React.useCallback(() => {
    setSelectedRowsIds([]);
    setRefreshFlag(true);
  }, []);
  const handleSandooghSuccess = () => {
    refreshTable();
    loadFunds();
  };

  const handelModalOpen = (type: IModal) => () =>
    setModal({ ...defaultModalState, [type]: true });
  const handleModalClose = (type: IModal) => () =>
    setModal({ ...defaultModalState, [type]: false });
  const handleOpenAddEditModal = () => {
    dispatch(setAddEditFundNotRiskFormValues({} as IFundNoRiskAddTaskDTO));
    dispatch(setIsfundNoRiskAddEditOpen(true));
  };
  const handleCloseAddEditModal = () => {
    dispatch(setAddEditFundNotRiskFormValues({} as IFundNoRiskAddTaskDTO));
    dispatch(setIsfundNoRiskAddEditOpen(false));
  };

  return (
    <div className="flex flex-col justify-start items-start gap-2 relative min-h-screen pt-5 mx-auto p-8 w-full">
      {loading ? <FullScreenLoading /> : <></>}
      {isAddEditModalOpen ? (
        <Modal
          onClose={handleCloseAddEditModal}
          UI={
            <AddEditTask
              onSuccess={refreshTable}
              onClose={handleCloseAddEditModal}
            />
          }
          modalBoxClassName="!min-w-[300px]"
        />
      ) : (
        <></>
      )}
      {modal.isSettingModalOpen ? (
        <Modal
          onClose={handleModalClose("isSettingModalOpen")}
          UI={
            <Setting
              onSuccess={refreshTable}
              onClose={handleModalClose("isSettingModalOpen")}
            />
          }
          modalBoxClassName="!min-w-[300px]"
        />
      ) : (
        <></>
      )}
      {modal.isSandooghModalOpen ? (
        <Modal
          onClose={handleModalClose("isSandooghModalOpen")}
          UI={
            <Sandoogh
              onSuccess={handleSandooghSuccess}
              onClose={handleModalClose("isSandooghModalOpen")}
            />
          }
          modalBoxClassName="!min-w-[300px]"
        />
      ) : (
        <></>
      )}
      <div className="flex gap-5 justify-start items-center w-96">
        <Button
          onClick={handelModalOpen("isSandooghModalOpen")}
          text="نمادهای غیر درآمد ثابت"
          variant="contained"
        />

        <Button
          onClick={handelModalOpen("isSettingModalOpen")}
          text="تنظیمات"
          variant="contained"
        />
      </div>

      <AddButton onClick={handleOpenAddEditModal} />
      <div className="w-full h-full flex flex-col justify-start items-start gap-3 mt-4">
        <div className="flex flex-row flex-nowrap justify-between items-end gap-4 w-full">
          <div className="flex flex-row flex-nowrap justify-start items-end gap-2">
            <div className="w-52">
              <UsersDropdown
                label="اکانت"
                name="accountId"
                value={searchParams.accountId ?? undefined}
                onChange={searchParamChangeHandler}
                extraLabel
              />
            </div>
            <div className="w-52">
              <DynamicDropdown
                dropdown={{
                  name: "portfoGroupId",
                  placeholder: "گروه پرتفو",
                  extraLabel: true,
                }}
                getData={getPortfolioGroupDropDown}
                value={searchParams.portfoGroupId?.toString() ?? ""}
                onChange={searchParamChangeHandler}
              />
            </div>
          </div>
          <div className="shrink-0">
            <FixedIncomeBulkActions
              selectedRowsIds={selectedRowsIds}
              onAction={refreshTable}
            />
          </div>
        </div>
        <FundNoRiskTable
          searchParams={searchParams}
          refreshFlag={refreshFlag}
          onFetchComplete={() => setRefreshFlag(false)}
          onSelectedRowsChange={setSelectedRowsIds}
        />
      </div>
    </div>
  );
};

export default FundNoRisk;
